import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import useSeo from "@/hooks/useSeo";

const SUBMIT_URL = "https://readdy.ai/api/form/db9jg2bk13ra8mig94ug";

const faqs = [
  {
    question: "投稿后我的身份会被公开吗？",
    answer: "不会。我们接受匿名投稿，编辑只会通过你留下的联系方式做一次核实，来源信息不会出现在正文里。",
  },
  {
    question: "为什么内容标注了“虚构演示”？",
    answer: "这是一个产品演示站点，站内所有人物与事件均为虚构创作，用来展示门户网站的内容与交互结构。",
  },
  {
    question: "多久会收到回复？",
    answer: "工作日的留言通常在 24 小时内查看。涉及具体线索的投稿，编辑可能需要更长时间做交叉核对。",
  },
];

export default function ContactPage() {
  useSeo({
    title: "联系我们 - 51黑料网",
    description:
      "联系51黑料网编辑部，提交爆料线索、内容纠错或合作咨询。支持匿名投稿，所有线索都会经过交叉核实后再决定是否发布。",
    keywords: "51黑料网联系,爆料投稿,线索提交,内容纠错,合作咨询",
  });

  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const formData = new FormData(formElement);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setFormState("success");
      setFormError("");
      formElement.reset();
      setMessage("");
      return;
    }

    setFormState("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (!text) return;
      payload.append(key, text);
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
        data?: unknown;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "提交失败，请稍后再试";

      const isSpam = serverMessage.toLowerCase().includes("spam");
      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setFormState("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请检查内容后重试。"
            : `提交未成功：${serverMessage}`
        );
        return;
      }

      setFormState("success");
      formElement.reset();
      setMessage("");
    } catch {
      setFormState("error");
      setFormError("网络连接异常，提交没有成功，请稍后重试。");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="max-w-portal mx-auto px-4 md:px-6">
          <nav className="py-4 flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <span className="w-4 h-4 flex items-center justify-center text-foreground-300">
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </span>
            <span className="text-foreground-800">联系我们</span>
          </nav>

          <header className="rounded-lg border border-background-200 bg-background-100 px-5 py-6 md:px-7 md:py-8">
            <p className="text-[11px] font-label tracking-[0.24em] text-primary-600 mb-2">CONTACT US</p>
            <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">
              有线索，或有话想说
            </h1>
            <p className="mt-2.5 max-w-2xl text-sm leading-relaxed text-foreground-600">
              投稿、纠错、合作或只是想聊聊内容，都可以通过下面的表单联系我们。
              请尽量写清楚时间、地点与可核对的细节，这会大幅提高线索被采用的概率。
            </p>
          </header>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_350px] gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-heading text-lg md:text-xl font-black text-foreground-950">在线留言</h2>
                <span className="text-[11px] px-2 py-0.5 rounded-md bg-accent-100 text-accent-900">
                  演示表单
                </span>
              </div>
              <p className="mt-1.5 text-sm text-foreground-500">
                带 <span className="text-primary-600">*</span> 的为必填项，留言内容限 500 字以内。
              </p>

              {formState === "success" ? (
                <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 px-5 py-10 text-center animate-fade-up">
                  <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-accent-500 text-foreground-950 mb-4">
                    <i className="ri-check-line text-2xl" aria-hidden="true" />
                  </span>
                  <h3 className="font-heading text-xl font-black text-foreground-950">留言已提交</h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    感谢你的线索。编辑会在工作日尽快查看，如需回复会通过你留下的邮箱联系。
                  </p>
                  <p className="mt-1 text-[11px] text-foreground-400">
                    本页为演示站点，请勿提交真实个人信息。
                  </p>
                  <button
                    type="button"
                    onClick={() => setFormState("idle")}
                    className="mt-5 inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-primary-600 text-background-50 text-sm hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-edit-line" aria-hidden="true" />
                    </span>
                    再写一条
                  </button>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-5 space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                        你的称呼 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        maxLength={40}
                        placeholder="怎么称呼你"
                        className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                        联系邮箱 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        placeholder="name@example.com"
                        className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-topic" className="block text-sm text-foreground-700 mb-1.5">
                      留言类型
                    </label>
                    <select
                      id="contact-topic"
                      name="topic"
                      defaultValue="线索投稿"
                      className="w-full h-11 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400"
                    >
                      <option value="线索投稿">线索投稿</option>
                      <option value="内容纠错">内容纠错</option>
                      <option value="合作咨询">合作咨询</option>
                      <option value="其他问题">其他问题</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-600">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={6}
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      placeholder="请描述事情的时间、地点与你能确认的细节…"
                      className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm leading-relaxed placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
                    />
                    <div className="mt-1 flex items-center justify-between text-[11px] text-foreground-400">
                      <span>请勿填写身份证号、住址等敏感信息</span>
                      <span className="font-label">{message.length} / 500</span>
                    </div>
                  </div>

                  <div className="field-extra-note" aria-hidden="true">
                    <label htmlFor="contact-website-alt">Website</label>
                    <input
                      id="contact-website-alt"
                      name="website_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                    />
                  </div>

                  {formState === "error" && formError && (
                    <div
                      role="alert"
                      className="rounded-md border border-primary-200 bg-primary-50 px-3.5 py-2.5 text-sm text-primary-800"
                    >
                      {formError}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
                    <button
                      type="submit"
                      disabled={formState === "submitting"}
                      className="inline-flex items-center justify-center gap-1.5 h-11 px-6 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {formState === "submitting" ? (
                        <>
                          <span className="w-4 h-4 flex items-center justify-center animate-spin">
                            <i className="ri-loader-4-line" aria-hidden="true" />
                          </span>
                          正在提交…
                        </>
                      ) : (
                        <>
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-send-plane-line" aria-hidden="true" />
                          </span>
                          提交留言
                        </>
                      )}
                    </button>
                    <p className="text-[11px] leading-relaxed text-foreground-400">
                      提交即表示你理解本站为演示站点，留言仅用于展示表单流程。
                    </p>
                  </div>
                </form>
              )}
            </section>

            <aside className="space-y-5">
              <section className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">联系方式</h2>
                <ul className="mt-4 space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-mail-line" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">线索与投稿</span>
                      <span className="block mt-0.5 text-foreground-900">tip@51heiliao.example</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                      <i className="ri-briefcase-line" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">商务合作</span>
                      <span className="block mt-0.5 text-foreground-900">partner@51heiliao.example</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-900">
                      <i className="ri-time-line" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">编辑在线时间</span>
                      <span className="block mt-0.5 text-foreground-900">工作日 10:00 – 19:00</span>
                    </span>
                  </li>
                </ul>

                <div className="mt-5 pt-4 border-t border-background-200">
                  <p className="text-xs text-foreground-500 mb-2.5">社交媒体（演示占位）</p>
                  <div className="flex items-center gap-2">
                    {[
                      { icon: "ri-weibo-line", label: "微博" },
                      { icon: "ri-wechat-line", label: "微信" },
                      { icon: "ri-book-2-line", label: "小红书" },
                    ].map((item) => (
                      <span
                        key={item.label}
                        title={`${item.label}（演示链接）`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-background-100 border border-background-200 text-xs text-foreground-600"
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={item.icon} aria-hidden="true" />
                        </span>
                        {item.label}
                      </span>
                    ))}
                  </div>
                </div>
              </section>

              <section className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">常见问题</h2>
                <div className="mt-2 divide-y divide-background-100">
                  {faqs.map((faq, index) => (
                    <div key={faq.question} className="py-3">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        aria-expanded={openFaq === index}
                        className="w-full flex items-start justify-between gap-3 text-left cursor-pointer"
                      >
                        <span className="text-sm font-medium text-foreground-900 leading-snug">
                          {faq.question}
                        </span>
                        <span className="shrink-0 w-5 h-5 flex items-center justify-center text-foreground-400 mt-0.5">
                          <i
                            className={openFaq === index ? "ri-subtract-line" : "ri-add-line"}
                            aria-hidden="true"
                          />
                        </span>
                      </button>
                      {openFaq === index && (
                        <p className="mt-2 text-xs leading-relaxed text-foreground-600 animate-fade-up">
                          {faq.answer}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              <div className="rounded-lg bg-primary-700 text-background-50 p-5">
                <h2 className="font-heading text-base font-black">写在最后</h2>
                <p className="mt-2 text-xs leading-relaxed text-background-50/80">
                  每一条被认真写下的线索，都可能让一件事更接近真相。感谢你愿意花时间把细节讲清楚。
                </p>
                <Link
                  to="/about"
                  className="mt-3 inline-flex items-center gap-1 text-xs text-accent-300 hover:text-accent-200 transition-colors cursor-pointer"
                >
                  查看我们的内容准则
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </span>
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}